/**
 * ExtrairRespostaIADialog - Leitor de propostas de fornecedor com IA (Parte B)
 * Fluxo: cola texto/PDF → IA extrai → tela de REVISÃO TOTAL:
 *  - itens da cotação com preço/disponibilidade editáveis (confirmação item a item)
 *  - itens fora da cotação com decisão do usuário (adicionar / substituição equivalente / ignorar)
 * A IA sugere, o usuário decide. Nada é salvo sem confirmação humana.
 * Aprendizado: equivalências e substituições confirmadas são gravadas por fornecedor.
 */
import React, { useState } from 'react';
import api from '../../lib/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, FileUp, X, CheckCircle2, AlertTriangle, ArrowLeft, PackagePlus } from 'lucide-react';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const CONF_BADGE = {
  alta: 'bg-emerald-100 text-emerald-700',
  media: 'bg-amber-100 text-amber-700',
  baixa: 'bg-red-100 text-red-700',
};

const ExtrairRespostaIADialog = ({ open, onClose, cotacao, fornecedorId, fornecedorNome, onAplicar }) => {
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [revisaoItens, setRevisaoItens] = useState([]);
  const [revisaoAdicionais, setRevisaoAdicionais] = useState([]);

  const reset = () => {
    setTexto(''); setArquivo(null); setResultado(null);
    setRevisaoItens([]); setRevisaoAdicionais([]);
  };
  const handleClose = () => { reset(); onClose(); };

  const handleProcessar = async () => {
    if (!texto.trim() && !arquivo) {
      toast.error('Cole o texto da proposta ou anexe um PDF');
      return;
    }
    setProcessando(true);
    try {
      const fd = new FormData();
      fd.append('cotacao_id', cotacao.id);
      fd.append('fornecedor_id', fornecedorId);
      if (texto.trim()) fd.append('texto', texto.trim());
      if (arquivo) fd.append('arquivo', arquivo);
      const res = await api.post('/compras/assistente/extrair-resposta', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const data = res.data;
      setResultado(data);
      // Monta a revisão de TODOS os itens da cotação (extraídos ou não)
      setRevisaoItens((cotacao.itens || []).map((it) => {
        const ext = (data.itens || []).find((e) => e.item_id === it.id) || {};
        return {
          item_id: it.id,
          descricao: it.descricao,
          quantidade: it.quantidade,
          preco: ext.preco_unitario != null ? String(ext.preco_unitario) : '',
          prazo: ext.prazo_entrega_dias != null ? String(ext.prazo_entrega_dias) : '',
          disponivel: ext.disponibilidade !== false,
          marca_ofertada: ext.marca_ofertada || '',
          confianca: ext.preco_unitario != null ? (ext.confianca || 'baixa') : null,
          trecho_origem: ext.trecho_origem || null,
          texto_fornecedor: ext.texto_fornecedor || null,
          substituicao_sugerida: ext.substituicao_sugerida || null,
        };
      }));
      // Itens fora da cotação: usuário decide o destino (com sugestão de vínculo ao cadastro)
      setRevisaoAdicionais((data.itens_adicionais || []).map((a) => ({
        descricao: a.descricao || '',
        preco: a.preco_unitario != null ? String(a.preco_unitario) : '',
        quantidade: a.quantidade != null ? String(a.quantidade) : '1',
        decisao: 'ignorar',
        substitui_item_id: '',
        produtos_sugeridos: a.produtos_sugeridos || [],
        produto_vinculo: (a.produtos_sugeridos && a.produtos_sugeridos[0]?.id) || '__novo__',
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar proposta com IA');
    } finally {
      setProcessando(false);
    }
  };

  const updateItem = (idx, field, value) => {
    setRevisaoItens((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const updateAdicional = (idx, field, value) => {
    setRevisaoAdicionais((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  };

  const adicionaisInvalidos = revisaoAdicionais.some(
    (a) => a.decisao === 'substituicao' && !a.substitui_item_id
  );

  const handleAplicarTudo = async () => {
    setAplicando(true);
    try {
      // 1. Adicionar novos itens à cotação (opção nativa do sistema: PUT itens)
      // Ciclo completo: vincula ao produto do cadastro OU faz cadastro rápido do produto novo
      let cotacaoAtualizada = null;
      let novosItens = [];
      const paraAdicionar = revisaoAdicionais.filter((a) => a.decisao === 'adicionar' && a.descricao.trim());
      if (paraAdicionar.length > 0) {
        for (const a of paraAdicionar) {
          let produtoId = null;
          let produtoNome = null;
          if (a.produto_vinculo === '__novo__') {
            // Cadastro rápido do produto
            const respProd = await api.post('/produtos', {
              nome: a.descricao.trim(),
              descricao: 'Criado via leitura de proposta de fornecedor (IA)',
            });
            produtoId = respProd.data?.id;
            produtoNome = respProd.data?.nome || a.descricao.trim();
          } else if (a.produto_vinculo && a.produto_vinculo !== '__nenhum__') {
            const sug = (a.produtos_sugeridos || []).find((s) => s.id === a.produto_vinculo);
            produtoId = a.produto_vinculo;
            produtoNome = sug?.nome || a.descricao.trim();
          }
          novosItens.push({
            id: crypto.randomUUID(),
            tipo: 'produto',
            produto_id: produtoId,
            produto_nome: produtoNome,
            descricao: a.descricao.trim(),
            quantidade: parseFloat(a.quantidade) || 1,
            unidade: 'UN',
            observacao: 'Adicionado via leitura de proposta (IA)',
            preco_unitario: parseFloat(a.preco) || 0,
          });
        }
        const itensParaCotacao = novosItens.map(({ preco_unitario, ...rest }) => rest);
        const resp = await api.put(`/cotacoes/${cotacao.id}`, {
          itens: [...(cotacao.itens || []), ...itensParaCotacao],
        });
        cotacaoAtualizada = resp.data;
      }

      // 2. Substituições equivalentes decididas pelo usuário
      const substituicoes = revisaoAdicionais
        .filter((a) => a.decisao === 'substituicao' && a.substitui_item_id)
        .map((a) => ({
          item_id: a.substitui_item_id,
          descricao: a.descricao.trim(),
          preco: a.preco !== '' ? parseFloat(a.preco) : null,
        }));

      // 3. Entrega tudo ao formulário (rascunho — usuário ainda salva manualmente)
      onAplicar({
        itens: revisaoItens,
        condicoes: resultado?.condicoes || {},
        novosItens,
        substituicoes,
        cotacaoAtualizada,
      });

      // 4. Aprendizado por fornecedor: equivalências de nome + substituições confirmadas
      const equivalencias = [
        ...revisaoItens
          .filter((i) => i.texto_fornecedor && i.preco !== '')
          .map((i) => ({ texto_fornecedor: i.texto_fornecedor, item_descricao: i.descricao })),
        ...substituicoes.map((s) => {
          const alvo = revisaoItens.find((i) => i.item_id === s.item_id);
          return { texto_fornecedor: s.descricao, item_descricao: alvo?.descricao || '' };
        }),
      ].filter((e) => e.item_descricao);
      if (equivalencias.length > 0) {
        api.post('/compras/assistente/equivalencias', { fornecedor_id: fornecedorId, equivalencias }).catch(() => {});
      }

      toast.success(
        `Rascunho preenchido!${novosItens.length ? ` ${novosItens.length} item(ns) adicionado(s) à cotação.` : ''} Revise e clique em Salvar Resposta.`
      );
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aplicar a extração');
    } finally {
      setAplicando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="extrair-ia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Registrar resposta com IA
          </DialogTitle>
          <DialogDescription>
            Fornecedor: <strong>{fornecedorNome}</strong> · Cotação <strong>{cotacao?.numero}</strong>.
            A IA lê e sugere — <strong>você decide</strong> item a item antes de aplicar.
          </DialogDescription>
        </DialogHeader>

        {!resultado ? (
          <div className="space-y-3">
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={'Cole aqui o e-mail ou mensagem do fornecedor...\n\nEx: "Boa tarde! Motor mercury 300 fica em 172.500 à vista ou 3x. Hélice tenho por 9.200. Prazo 45 dias, frete por nossa conta."'}
              rows={8}
              className="text-sm"
              data-testid="extrair-ia-texto"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50">
                <FileUp className="w-4 h-4" />
                {arquivo ? arquivo.name : 'Ou anexe o PDF/foto da proposta'}
                <input
                  type="file"
                  accept=".pdf,.txt,image/*,.heic,.heif"
                  className="hidden"
                  onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                  data-testid="extrair-ia-arquivo"
                />
              </label>
              {arquivo && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setArquivo(null)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="extrair-ia-resultado">
            {/* ===== 1. Itens da cotação — verificação total ===== */}
            <div>
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                1 · Itens da cotação — confirme cada valor
              </h4>
              <div className="space-y-2">
                {revisaoItens.map((it, idx) => (
                  <div key={it.item_id} className="border rounded-lg p-3 bg-slate-50/60" data-testid={`extrair-ia-item-${idx}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {it.descricao} <span className="text-xs font-normal text-slate-500">× {it.quantidade}</span>
                        </p>
                        {it.trecho_origem && (
                          <p className="text-[11px] text-slate-400 italic truncate">"{it.trecho_origem}"</p>
                        )}
                        {it.substituicao_sugerida && (
                          <p className="text-[11px] text-orange-600 flex items-center gap-1 mt-0.5">
                            <AlertTriangle className="w-3 h-3" /> Fornecedor sugeriu substituto: {it.substituicao_sugerida}
                            <span className="text-slate-400 font-normal">(entrará como sugestão de alternativa no formulário)</span>
                          </p>
                        )}
                      </div>
                      {it.confianca ? (
                        <Badge className={`text-[10px] shrink-0 ${CONF_BADGE[it.confianca]}`}>
                          {it.confianca === 'alta' ? '✓' : '⚠'} {it.confianca}
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] shrink-0 bg-slate-200 text-slate-600">não encontrado</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-500">R$</span>
                        <Input
                          type="number" min="0" step="0.01"
                          value={it.preco}
                          onChange={(e) => updateItem(idx, 'preco', e.target.value)}
                          placeholder="0,00"
                          className="w-28 h-8 text-xs bg-white"
                          disabled={!it.disponivel}
                          data-testid={`extrair-ia-preco-${idx}`}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-500">Prazo</span>
                        <Input
                          type="number" min="0"
                          value={it.prazo}
                          onChange={(e) => updateItem(idx, 'prazo', e.target.value)}
                          placeholder="dias"
                          className="w-20 h-8 text-xs bg-white"
                          disabled={!it.disponivel}
                        />
                      </div>
                      <Select
                        value={it.disponivel ? 'disponivel' : 'indisponivel'}
                        onValueChange={(v) => updateItem(idx, 'disponivel', v === 'disponivel')}
                      >
                        <SelectTrigger className="w-56 h-8 text-xs bg-white" data-testid={`extrair-ia-disponibilidade-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="disponivel">Disponível</SelectItem>
                          <SelectItem value="indisponivel">Não disponível neste fornecedor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ===== 2. Itens fora da cotação — decisão do usuário ===== */}
            {revisaoAdicionais.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <PackagePlus className="w-3.5 h-3.5" />
                  2 · Itens fora da cotação — você decide o destino
                </h4>
                <div className="space-y-2">
                  {revisaoAdicionais.map((a, idx) => (
                    <div key={idx} className="border border-amber-200 rounded-lg p-3 bg-amber-50/50" data-testid={`extrair-ia-adicional-${idx}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <p className="text-sm font-medium text-slate-900 flex-1 min-w-0">{a.descricao}</p>
                        <div className="flex items-center gap-1">
                          <span className="text-[11px] text-slate-500">R$</span>
                          <Input
                            type="number" min="0" step="0.01"
                            value={a.preco}
                            onChange={(e) => updateAdicional(idx, 'preco', e.target.value)}
                            className="w-24 h-7 text-xs bg-white"
                          />
                          <span className="text-[11px] text-slate-500 ml-1">Qtd</span>
                          <Input
                            type="number" min="0" step="0.01"
                            value={a.quantidade}
                            onChange={(e) => updateAdicional(idx, 'quantidade', e.target.value)}
                            className="w-16 h-7 text-xs bg-white"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={a.decisao} onValueChange={(v) => updateAdicional(idx, 'decisao', v)}>
                          <SelectTrigger className="w-64 h-8 text-xs bg-white" data-testid={`extrair-ia-decisao-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ignorar">Ignorar este item</SelectItem>
                            <SelectItem value="adicionar">Adicionar como novo item à cotação</SelectItem>
                            <SelectItem value="substituicao">É substituição equivalente de…</SelectItem>
                          </SelectContent>
                        </Select>
                        {a.decisao === 'substituicao' && (
                          <Select value={a.substitui_item_id} onValueChange={(v) => updateAdicional(idx, 'substitui_item_id', v)}>
                            <SelectTrigger className="flex-1 min-w-[200px] h-8 text-xs bg-white" data-testid={`extrair-ia-substitui-${idx}`}>
                              <SelectValue placeholder="Escolha o item da cotação" />
                            </SelectTrigger>
                            <SelectContent>
                              {(cotacao?.itens || []).map((it) => (
                                <SelectItem key={it.id} value={it.id}>{it.descricao}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      {/* Vínculo com o cadastro de produtos (cruzamento / cadastro rápido) */}
                      {a.decisao === 'adicionar' && (
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          <span className="text-[11px] text-slate-600">Produto do cadastro:</span>
                          <Select value={a.produto_vinculo} onValueChange={(v) => updateAdicional(idx, 'produto_vinculo', v)}>
                            <SelectTrigger className="flex-1 min-w-[220px] h-8 text-xs bg-white" data-testid={`extrair-ia-produto-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(a.produtos_sugeridos || []).map((s) => (
                                <SelectItem key={s.id} value={s.id}>🔗 {s.nome}{s.sku ? ` (${s.sku})` : ''}</SelectItem>
                              ))}
                              <SelectItem value="__novo__">➕ Cadastro rápido: criar produto novo</SelectItem>
                              <SelectItem value="__nenhum__">Sem vínculo com o cadastro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== 3. Condições ===== */}
            {resultado.condicoes && (
              <div className="p-3 rounded-lg bg-slate-50 border text-xs space-y-1" data-testid="extrair-ia-condicoes">
                <p className="font-semibold text-slate-700 mb-1">
                  {revisaoAdicionais.length > 0 ? '3' : '2'} · Condições identificadas (serão aplicadas ao formulário):
                </p>
                {resultado.condicoes.condicao_pagamento && <p>💳 Pagamento: {resultado.condicoes.condicao_pagamento}</p>}
                {resultado.condicoes.prazo_entrega && <p>🚚 Prazo de entrega: {resultado.condicoes.prazo_entrega}</p>}
                {resultado.condicoes.frete_tipo && (
                  <p>📦 Frete: {resultado.condicoes.frete_tipo.toUpperCase()}{resultado.condicoes.frete_valor > 0 ? ` (${fmtBRL(resultado.condicoes.frete_valor)})` : ''}</p>
                )}
                {resultado.condicoes.validade_proposta && <p>📅 Validade: {resultado.condicoes.validade_proposta}</p>}
                {resultado.condicoes.prazo_garantia && <p>🛡 Garantia: {resultado.condicoes.prazo_garantia}</p>}
                {resultado.condicoes.desconto > 0 && <p>🏷 Desconto: {fmtBRL(resultado.condicoes.desconto)}</p>}
                {resultado.condicoes.observacao && <p className="text-slate-500 italic">{resultado.condicoes.observacao}</p>}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          {resultado ? (
            <>
              <Button variant="outline" onClick={() => setResultado(null)} data-testid="extrair-ia-voltar">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Voltar
              </Button>
              <Button
                onClick={handleAplicarTudo}
                disabled={aplicando || adicionaisInvalidos}
                title={adicionaisInvalidos ? 'Escolha o item da cotação para cada substituição' : ''}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="extrair-ia-aplicar"
              >
                {aplicando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Aplicar tudo ao formulário
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button
                onClick={handleProcessar}
                disabled={processando}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="extrair-ia-processar"
              >
                {processando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {processando ? 'Lendo proposta...' : 'Processar com IA'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExtrairRespostaIADialog;
